import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { finalize, map, take } from 'rxjs/operators';

import { AnalyticsService } from '../analytics/analytics.service';
import { ConfigServerService } from '../config/server/config-server.service';

import { CacheService } from '../cache';
import { SessionStorageService } from '../web-storage';
import { Auth } from './auth';
import { JwtService } from './jwt.service';

import { ChatHistoryStateService } from '../../../domains/aya/services/chat-history-state.service';
import { AyaService } from '../../../domains/aya/services/aya.service';
import { ChatPanelService } from '../../../domains/aya/services/chat-panel.service';
import { Empresa } from '../../../home/empresa';
import { Representante } from '../../../home/pessoa/representante';
import { Usuario } from '../../../home/usuario';
import { UsuarioAvatarService } from '../../../home/usuario/avatar/services/usuario-avatar.service';
import { UsuarioEmailAssinaturaService } from '../../../home/usuario/email/services';
import { EnumNivelVisualizacaoResultadoPedido } from '../../../home/usuario/enums/nivel-visualizacao-resultado-pedido.enum';
import { getUsuarioPerfil, isPerfilAdministrador, isPerfilAtendente, isPerfilExpedicao, isPerfilFinanceiro, isPerfilGerente, isPerfilPCP, isPerfilRepresentante, UsuarioPerfil } from '../../../home/usuario/usuario-perfil';
import { VisualizacaoStorageService } from '../visualizacao-storage';


@Injectable({
    providedIn: 'root'
})
export class AuthService {

    constructor(private router: Router,
                private http: HttpClient,
                private configServerService: ConfigServerService,
                private jwtService: JwtService,
                private visualizacaoStorageService: VisualizacaoStorageService,
                private sessionStorageService: SessionStorageService,
                private cacheService: CacheService,
                private usuarioAvatarService: UsuarioAvatarService,
                private usuarioEmailAssinaturaService: UsuarioEmailAssinaturaService,
                private chatPanelService: ChatPanelService,
                private ayaService: AyaService,
                private analyticsService: AnalyticsService,
                private chatHistoryState: ChatHistoryStateService) { }

    /**
     * Verifica se o usuário está autenticado
     */
    get isAuth(): boolean {
        // Se não possui o token de acesso ou o mesmo estiver expirado, retornamos como usuário não autenticado
        if (!this.jwtService.accessToken || this.jwtService.isTokenExpired()) {
            return false;
        }
        return true;
    }

    /**
     * Realiza o login na aplicação
     */
    login(auth: Auth, stayConnected: boolean): Observable<Object> {
        return this.http.post(`${this.configServerService.getPathApi()}/auth`, auth).pipe(
            map(response => {
                this.limparEstadoDaSessao();
                // Adicionando o JWT ao Web Storage
                this.jwtService.setAccessToken((response as any).access_token, stayConnected);
                this.afterLogin();

                return response;
            })
        );
    }

    private afterLogin() {
        const usuario = this.getUsuario();
        if (usuario) {
            // Setando o avatar do usuário
            this.usuarioAvatarService.next(usuario.avatarUrl);
            // Setando a assinatura de e-mail do usuário
            this.usuarioEmailAssinaturaService.next(usuario.urlAssinaturaEmail);
            this.analyticsService.setUserProfile(this.getPerfilUsuario());
        }
    }

  getDefaultRoute(): string[] {
    // Perfis com rotas específicas
    if (this.isExpedicao()) {
return ['/', 'expedicao'];
}
    if (this.isFinanceiro()) {
return ['/', 'consultas', 'analise-financeira'];
}
    if (this.isPCP()) {
return ['/', 'pedidos'];
}

    // Se tem acesso AI, vai para ceppem-ai
    if (this.canAccessAIPage()) {
      return ['/', 'ceppem-ai'];
    }

    // Fallback para dashboard
    return ['/', 'dashboard'];
  }

    /**
     * Realiza o logout na aplicação
     */
    logout() {
        return this.http.post(`${this.configServerService.getPathApi()}/auth/logout`, {})
                    .pipe(
                        take(1),
                        finalize(() => this.removeSession())
                    )
                    .subscribe({ error: () => {} });
    }

    /** Remove a sessão do usuário */
    removeSession() {
        this.limparEstadoDaSessao();

        setTimeout(() => {
            this.router.navigate(['/login']);
        }, 100);
    }

    private limparEstadoDaSessao(): void {
        this.jwtService.remove();
        this.visualizacaoStorageService.clear();
        // Limpando tudo do webstorage
        this.sessionStorageService.clearAll();
        // Limpando o cache local
        this.cacheService.clearAll();
        // Fechando e limpando o chat
        this.chatPanelService.close();
        this.ayaService.clear();
        this.chatHistoryState.reset();
    }


    /**
     * Pega o usuário atualmente logado
     */
    getUsuario(): Usuario {
        const tokenDecoded = this.jwtService.decodeToken();
        if (!tokenDecoded) {
            return null;
        }
        return tokenDecoded.usuario;
    }

    /**
     * Pega o id do usuário atualmente logado
     */
    getIdUsuario(): number {
        const usuario = this.getUsuario();
        if (!usuario) {
            return null;
        }
        return usuario.id;
    }

    /**
     * Pega o id da empresa do usuário atualmente logado
     */
    getIdEmpresa(): number {
        const usuario = this.getUsuario();
        if (!usuario) {
            return null;
        }
        return usuario.idEmpresa;
    }

    /**
     * Pega a empresa do usuário atualmente logado
     */
    getEmpresa(): Empresa {
        const usuario = this.getUsuario();
        if (!usuario) {
            return null;
        }
        return usuario.empresa;
    }

     /**
      * Pega o perfil do usuário atualmente logado
      */
    getPerfilUsuario(): UsuarioPerfil {
        const idPerfil = this.getIdPerfilUsuario();
        return getUsuarioPerfil(idPerfil);
    }

     /**
      * Pega o id do perfil do usuário atualmente logado
      */
    getIdPerfilUsuario(): number {
        const usuario = this.getUsuario();
        if (!usuario) {
            return null;
        }
        return usuario.perfil;
    }

    /**
     * Retornar o e-mail do usuário atualmente logado
     */
    getEmailUsuario(): string {
        const usuario = this.getUsuario();
        if (!usuario) {
            return null;
        }
        return usuario.login;
    }

    /** Verifica se o usuário informado é o usuário logado */
    isUsuarioLogado(idUsuario: number): boolean {
        const idUsuarioLogado = this.getIdUsuario();
        return idUsuarioLogado === idUsuario;
    }

    /** Verifica se o usuário atualmente logado possui o perfil de Administrador */
    isAdministrador(): boolean {
        const usuario = this.getUsuario();
        if (!usuario) {
            return false;
        }
        return isPerfilAdministrador(usuario.perfil);
    }

    /** Verifica se o usuário atualmente logado possui o perfil de Representante */
    isRepresentante(): boolean {
        const usuario = this.getUsuario();
        if (!usuario) {
            return false;
        }
        return isPerfilRepresentante(usuario.perfil);
    }

    /** Verifica se o usuário atualmente logado possui o perfil de Gerente */
    isGerente(): boolean {
        const usuario = this.getUsuario();
        if (!usuario) {
            return false;
        }
        return isPerfilGerente(usuario.perfil);
    }

    /** Verifica se o usuário atualmente logado possui o perfil de atendente */
    isAtendente(): boolean {
        const usuario = this.getUsuario();
        if (!usuario) {
            return false;
        }
        return isPerfilAtendente(usuario.perfil);
    }

    /** Verifica se o usuário atualmente logado possui o perfil de Expedicao */
    isExpedicao(): boolean {
        const usuario = this.getUsuario();
        if (!usuario) {
            return false;
        }
        return isPerfilExpedicao(usuario.perfil);
    }

    /** Verifica se o usuário atualmente logado possui o perfil de Financeiro */
    isFinanceiro(): boolean {
        const usuario = this.getUsuario();
        if (!usuario) {
            return false;
        }
        return isPerfilFinanceiro(usuario.perfil);
    }

    /** Verifica se o usuário atualmente logado possui o perfil de PCP */
    isPCP(): boolean {
        const usuario = this.getUsuario();
        if (!usuario) {
            return false;
        }
        return isPerfilPCP(usuario.perfil);
    }


    /** Pega o representante do usuário atualmente logado */
    getRepresentante(): Representante {
        const usuario = this.getUsuario();
        if (!usuario) {
            return null;
        }
        return usuario.representante ? usuario.representante : null;
    }

    /** Pega o id do representante do usuário atualmente logado */
    getIdRepresentante(): number {
        const usuario = this.getUsuario();
        if (!usuario) {
            return null;
        }
        const representante = usuario.representante;
        if (!representante) {
            return null;
        }
        return representante.id;
    }

    /** Verifica se o usuário tem permissão para editar clientes */
    podeEditarCliente(): boolean {
        const usuario = this.getUsuario();
        if (!usuario) {
            return false;
        }
        return usuario.podeEditarCliente ?? false;
    }

    /** Verifica se o usuário pode editar os segmentos do cliente */
    podeEditarClienteSegmentos(): boolean {
        const usuario = this.getUsuario();
        if (!usuario) {
            return false;
        }
        return usuario.podeEditarClienteSegmentos ?? false;
    }

    /** Verifica se o usuário tem permissão para lançar e editar os valores das metas */
    podeEditarMetas(): boolean {
        const usuario = this.getUsuario();
        if (!usuario) {
            return false;
        }
        return usuario.podeEditarMetas ?? false;
    }

    /** Verifica se o usuário tem permissão para alterar os dados de PLM do produto */
    podeEditarDadosPLM(): boolean {
        const usuario = this.getUsuario();
        if (!usuario) {
            return false;
        }
        return usuario.podeEditarDadosPLM ?? false;
    }

    /** Verifica se o usuário tem permissão para acompanhar a fila de gates do PLM */
    podeAcompanharGatesPLM(): boolean {
        const usuario = this.getUsuario();
        if (!usuario) {
            return false;
        }
        return usuario.podeAcompanharGatesPLM ?? false;
    }

    /** Verifica se o usuário tem permissão para registrar decisões de gate do PLM */
    podeDecidirGatesPLM(): boolean {
        const usuario = this.getUsuario();
        if (!usuario) {
            return false;
        }
        return usuario.podeDecidirGatesPLM ?? false;
    }

    /** Verifica se o usuário tem permissão para encerrar um produto no PLM */
    podeEncerrarProdutoPLM(): boolean {
        const usuario = this.getUsuario();
        if (!usuario) {
            return false;
        }
        return usuario.podeEncerrarProdutoPLM ?? false;
    }

    /**
     * Qualquer uma das três permissões basta: exigir também a de acompanhamento deixaria
     * quem decide sem a fila por um checkbox esquecido no cadastro.
     */
    podeAcessarGovernancaPLM(): boolean {
        return (
            this.podeAcompanharGatesPLM() ||
            this.podeDecidirGatesPLM() ||
            this.podeEncerrarProdutoPLM()
        );
    }

    /** Verifica se o usuário tem permissão para confirmar cadastrar clientes do exterior */
    podeCadastrarClienteExterior(): boolean {
        const usuario = this.getUsuario();
        if (!usuario) {
            return false;
        }
        return usuario.podeCadastrarClienteExterior ?? false;
    }

    /** Verifica se o usuário tem permissão para aprovar, reprovar e reabrir as revisões das Análises de Mercado */
    podeGerenciarRevisoesAnalisesMercado(): boolean {
        const usuario = this.getUsuario();
        if (!usuario) {
            return false;
        }
        return usuario.podeGerenciarRevisoesAnalisesMercado;
    }

    /** Verifica se o usuário tem permissão para aprovar e reprovar campanhas */
    podeAnalisarCampanhas(): boolean {
        const usuario = this.getUsuario();
        if (!usuario) {
            return false;
        }
        return usuario.podeAnalisarCampanhas;
    }

    /** Verifica se o usuário tem permissão para confirmar pedidos "Não Confirmados" */
    podeConfirmarPedido(): boolean {
        const usuario = this.getUsuario();
        if (!usuario) {
            return false;
        }
        return usuario.podeConfirmarPedido;
    }

    /** Verifica se o usuário tem permissão para liberar pedidos */
    podeLiberarPedido(): boolean {
        const usuario = this.getUsuario();
        if (!usuario) {
            return false;
        }
        return usuario.podeLiberarPedido;
    }

    /** Verifica se o usuário tem permissão para editar pedidos não liberados */
    podeEditarPedidoNaoLiberado(): boolean {
        const usuario = this.getUsuario();
        if (!usuario) {
            return false;
        }
        return usuario.podeEditarPedidoNaoLiberado;
    }

    /** Verifica se o usuário tem permissão para editar pedidos confirmados */
    podeEditarPedidoConfirmado(): boolean {
        const usuario = this.getUsuario();
        if (!usuario) {
            return false;
        }
        return usuario.podeEditarPedidoConfirmado;
    }

    /** Verifica se o usuário tem permissão para editar as comissões dos pedidos */
    podeEditarPedidoComissao(): boolean {
        const usuario = this.getUsuario();
        if (!usuario) {
            return false;
        }
        return usuario.podeEditarPedidoComissao;
    }

    /** Verifica se o usuário tem permissão para editar as entregas dos itens dos pedidos */
    podeEditarEntregas(): boolean {
        const usuario = this.getUsuario();
        if (!usuario) {
            return false;
        }
        return usuario.podeEditarEntregas;
    }

    /** Verifica se o usuário tem permissão para exportar pedidos para o ERP */
    podeExportarErp(): boolean {
        const usuario = this.getUsuario();
        if (!usuario) {
            return false;
        }
        return usuario.podeExportarErp;
    }

    /** Verifica se o usuário tem permissão para salvar cadastros de clientes, representantes e transportadoras mesmo com o CNPJ/CPF inválido */
    podeSalvarCnpjCpfInvalido(): boolean {
        const usuario = this.getUsuario();
        if (!usuario) {
            return false;
        }
        return usuario.podeSalvarCnpjCpfInvalido;
    }

    /** Verifica se o usuário tem pode importar informações do ERP */
    podeImportarErp(): boolean {
        const usuario = this.getUsuario();
        if (!usuario) {
            return false;
        }
        return usuario.podeImportarErp;
    }

    /** Verifica se o usuário tem pode importar informações do E-commerce */
    podeImportarEcommerce(): boolean {
        const usuario = this.getUsuario();
        if (!usuario) {
            return false;
        }
        return usuario.podeImportarEcommerce;
    }

    /** Verifica se o usuário tem permissão para revisar pedidos/orçamentos */
    podeRevisarPedidoOrcamento(): boolean {
        const usuario = this.getUsuario();
        if (!usuario || !usuario.podeRevisarPedidoOrcamento) {
            return false;
        }
        return usuario.podeRevisarPedidoOrcamento;
    }

    /** Verifica se o usuário tem permissão para informar a validade da proposta comercial */
    podeInformarValidadePropostaComercialPedido(): boolean {
        const usuario = this.getUsuario();
        if (!usuario || !usuario.podeInformarValidadePropostaComercial) {
            return false;
        }
        return usuario.podeInformarValidadePropostaComercial;
    }

    /** Verifica se o usuário tem permissão para simular pedidos */
    podeSimularPedido(): boolean {
        const usuario = this.getUsuario();
        if (!usuario || !usuario.podeSimularPedido) {
            return false;
        }
        return usuario.podeSimularPedido;
    }

    /** Retorna o nível de visualização do resultado do pedido do usuário logado */
    getNivelVisualizacaoResultadoPedido(): number {
        const usuario = this.getUsuario();
        if (!usuario) {
            return EnumNivelVisualizacaoResultadoPedido.SemAcesso;
        }
        return usuario.nivelVisualizacaoResultadoPedido ?? EnumNivelVisualizacaoResultadoPedido.SemAcesso;
    }

    /** Verifica se o usuário pode visualizar informações de Qualidade de Valor (nível >= 1) */
    podeVisualizarQV(): boolean {
        return this.getNivelVisualizacaoResultadoPedido() >= EnumNivelVisualizacaoResultadoPedido.QualidadeDeValor;
    }

    /** Verifica se o usuário pode visualizar o D.R.P resumido — margem de lucro (nível >= 2) */
    podeVisualizarDRP(): boolean {
        return this.getNivelVisualizacaoResultadoPedido() >= EnumNivelVisualizacaoResultadoPedido.DRP;
    }

    /** Verifica se o usuário pode visualizar o D.R.P completo com composição de custos (nível >= 3) */
    podeVisualizarDRPCustos(): boolean {
        return this.getNivelVisualizacaoResultadoPedido() >= EnumNivelVisualizacaoResultadoPedido.DRPCustos;
    }

    /** Se pode acessar e gerenciar as tabelas de preços */
    podeAcessarTabelasPrecos(): boolean {
        const usuario = this.getUsuario();
        if (!usuario) {
            return false;
        }
        return usuario.acessoTabelasPrecos;
    }

    canAccessAIPage(): boolean {
      const usuario = this.getUsuario();
      if (!usuario) {
return false;
}

      return usuario.acessoAI;
    }

    /** Se pode acessar as configurações do sistema */
    podeAcessarConfiguracoes(): boolean {
        const usuario = this.getUsuario();
        if (!usuario) {
            return false;
        }
        return usuario.acessoConfiguracoes;
    }

    podeAcessarCentralConfiguracoes(): boolean {
        const usuario = this.getUsuario();
        if (!usuario) {
            return false;
        }
        return usuario.acessoConfiguracoes;
    }

    /**
     * Pega o ID da empresa no Sogivendas
     */
    getSogivendasId(): number {
        const tokenDecoded = this.jwtService.decodeToken();
        if (!tokenDecoded) {
            return null;
        }
        return tokenDecoded.sogivendasId;
    }

    /** Verifica se a empresa é a Nutrata */
    isNutrata(): boolean {
        const sogivendasId = this.getSogivendasId();
        if (!sogivendasId) {
            return false;
        }
        return sogivendasId === 1;
    }
    /** Verifica se a empresa é a Rafitec */
    isRafitec(): boolean {
        const sogivendasId = this.getSogivendasId();
        if (!sogivendasId) {
            return false;
        }
        return sogivendasId === 2;
    }
    /** Verifica se a empresa é a Açaí Amazonas */
    isAcaiAmazonas(): boolean {
        const sogivendasId = this.getSogivendasId();
        if (!sogivendasId) {
            return false;
        }
        return sogivendasId === 3;
    }
    /** Verifica se a empresa é a Propex */
    isPropex(): boolean {
        const sogivendasId = this.getSogivendasId();
        if (!sogivendasId) {
            return false;
        }
        return sogivendasId === 5;
    }
    /** Verifica se a empresa é a Agroplás */
    isAgroplas(): boolean {
        const sogivendasId = this.getSogivendasId();
        if (!sogivendasId) {
            return false;
        }
        return sogivendasId === 6;
    }
    /** Verifica se a empresa é a Rafitec Grass */
    isRafitecGrass(): boolean {
        const sogivendasId = this.getSogivendasId();
        if (!sogivendasId) {
            return false;
        }
        return sogivendasId === 7;
    }
}
